import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { EASE } from "@/lib/site";
import { goToContact } from "@/lib/scroll";

const FILTERS = ["Todos", "Sites", "Landing Pages", "E-commerce", "Aplicativos", "Sistemas"];

const PROJECTS = [
  { id: "barbearia", title: "Site para barbearia", cat: "Sites", type: "Site", desc: "Apresentação, serviços e agendamento online.", img: "/img/pf_barbearia.webp" },
  { id: "restaurante", title: "Site para restaurante", cat: "Sites", type: "Site", desc: "Cardápio, ambiente e reservas em destaque.", img: "/img/pf_restaurante.webp" },
  { id: "institucional", title: "Site institucional", cat: "Sites", type: "Site", desc: "Presença profissional para empresas e escritórios.", img: "/img/pf_institucional.webp" },
  { id: "landing", title: "Landing page", cat: "Landing Pages", type: "Landing Page", desc: "Página de alta conversão para campanhas.", img: "/img/pf_landing.webp" },
  { id: "loja", title: "Loja virtual", cat: "E-commerce", type: "Loja Virtual", desc: "Catálogo, carrinho e checkout para vender online.", img: "/img/pf_loja.webp" },
  { id: "app", title: "Aplicativo de agendamento", cat: "Aplicativos", type: "Aplicativo", desc: "App mobile com agenda, perfil e notificações.", img: "/img/pf_app.webp" },
  { id: "dashboard", title: "Dashboard de gestão", cat: "Sistemas", type: "Sistema", desc: "Indicadores e relatórios em tempo real.", img: "/img/pf_dashboard.webp" },
  { id: "sistema", title: "Sistema web para clínica", cat: "Sistemas", type: "Sistema", desc: "Pacientes, agenda e cadastros em um só lugar.", img: "/img/pf_sistema.webp" },
];

const Card = ({ p }) => (
  <motion.article
    layout
    data-testid={`portfolio-card-${p.id}`}
    initial={{ opacity: 0, scale: 0.94 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94 }}
    transition={{ duration: 0.5, ease: EASE }}
    className="group flex flex-col overflow-hidden rounded-3xl border border-white/[0.07] bg-ws-card transition-[border-color] duration-300 hover:border-ws-lime/35"
  >
    <div className="relative aspect-[16/10] overflow-hidden bg-black">
      <img src={p.img} alt={`${p.title} — projeto demonstrativo`} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.07]" />
      <div className="absolute inset-0 bg-gradient-to-t from-ws-card via-transparent to-transparent opacity-80" />
      <span className="absolute left-4 top-4 rounded-full border border-ws-lime/40 bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ws-glow backdrop-blur-md">
        Projeto demonstrativo
      </span>
    </div>
    <div className="flex flex-1 flex-col p-6">
      <p className="font-mono text-[11px] uppercase tracking-widest text-ws-muted">{p.cat}</p>
      <h3 className="mt-2 text-lg font-medium tracking-tight">{p.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-ws-muted">{p.desc}</p>
      <button data-testid={`portfolio-cta-${p.id}`} onClick={() => goToContact(p.type)} className="mt-auto flex items-center gap-2 pt-5 text-sm font-semibold text-ws-lime">
        Quero um projeto assim
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
      </button>
    </div>
  </motion.article>
);

const YourProjectCard = () => (
  <motion.button
    layout
    data-testid="portfolio-your-project"
    onClick={() => goToContact()}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5, ease: EASE }}
    className="group relative flex min-h-[340px] flex-col items-center justify-center overflow-hidden rounded-3xl border border-dashed border-ws-lime/40 bg-ws-lime/[0.03] p-8 text-center"
  >
    <div className="dot-grid mask-radial pointer-events-none absolute inset-0" />
    <span className="relative grid h-16 w-16 place-items-center rounded-full bg-ws-lime text-ws-bg transition-transform duration-500 group-hover:rotate-90 group-hover:scale-110">
      <Plus size={28} />
    </span>
    <h3 className="relative mt-6 text-xl font-medium">Seu projeto aqui</h3>
    <p className="relative mt-2 text-sm text-ws-muted">Começar meu projeto com a WebStation</p>
  </motion.button>
);

export const Portfolio = () => {
  const [filter, setFilter] = useState("Todos");
  const list = filter === "Todos" ? PROJECTS : PROJECTS.filter((p) => p.cat === filter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading index="07" eyebrow="Portfólio" title="Projetos que mostram o que podemos criar." sub="Exemplos demonstrativos de soluções que a WebStation desenvolve. Em breve, novos projetos reais." testid="portfolio-title" />
          <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Filtrar portfólio">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`relative shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${filter === f ? "text-ws-bg" : "text-ws-muted hover:text-ws-text"}`}
              >
                {filter === f && <motion.span layoutId="pf-pill" className="absolute inset-0 rounded-full bg-ws-lime" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                {filter !== f && <span className="absolute inset-0 rounded-full border border-white/10" />}
                <span className="relative">{f}</span>
              </button>
            ))}
          </div>
        </div>
        <motion.div layout className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {list.map((p) => (
              <Card key={p.id} p={p} />
            ))}
          </AnimatePresence>
          <YourProjectCard />
        </motion.div>
      </div>
    </section>
  );
};
